import {
  useCallback, useEffect, useRef, useState,
} from 'react'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'

import { NOOP } from 'Utils/stableDefaults'

import { COMMIT_ERROR_KEY, REDIRECT_DELAY_MS, pageErrorMessageKey } from '../Constants/upgradeErrors'

const FETCH_INTERVAL_MS = 2000
const FETCH_TIMEOUT_MS = 60000

const COMMIT_RETRY_MESSAGES = {
  quote_changed: `${COMMIT_ERROR_KEY}.quote_changed`,
  target_not_allowed: `${COMMIT_ERROR_KEY}.target_not_allowed`,
  invalid_target: 'subscription_checkout.intent_update_error',
}

// Any other failed code is upgrade_failed: money may have moved, so a retry could charge twice.
const ATTEMPT_RETRY_MESSAGES = {
  payment_declined: 'subscription_checkout.payment_error_toast',
  quote_changed: `${COMMIT_ERROR_KEY}.quote_changed`,
}

const EXPECTED_COMMIT_CODES = [...Object.keys(COMMIT_RETRY_MESSAGES), 'upgrade_in_progress']

const errorData = (error) => error?.response?.data || {}
const isExpectedCommitError = (error) => {
  const { code, redirectTo } = errorData(error)
  return EXPECTED_COMMIT_CODES.includes(code) || Boolean(redirectTo)
}
const isAttemptNotFound = (error) => errorData(error).code === 'attempt_not_found'

export const ATTEMPT_PHASE = { IDLE: 'idle', PROCESSING: 'processing', FAILED: 'failed' }

// Commit + result polling of contract 124g39pcvuz: POST { target, quotedAmount } -> 202 attemptId,
// then GET ?attempt= every 2 s until the attempt is terminal or 60 s pass.
export default function useUpgradeAttempt({
  commitUrl, returnPath, translate, onRetry = NOOP, onQuoteStale = NOOP, onFailure = NOOP,
}) {
  const { postData } = useApiPost()
  const { getData } = useApiGet()
  const { showError } = useNotifications()
  const [phase, setPhase] = useState(ATTEMPT_PHASE.IDLE)
  const fetchTimerRef = useRef(null)

  useEffect(() => () => clearTimeout(fetchTimerRef.current), [])

  const fail = useCallback((messageKey, code) => {
    showError(translate(messageKey))
    onFailure(code)
  }, [showError, translate, onFailure])

  const retryWithNewQuote = useCallback((messageKey, code) => {
    setPhase(ATTEMPT_PHASE.IDLE)
    fail(messageKey, code)
    onQuoteStale()
    onRetry()
  }, [fail, onQuoteStale, onRetry])

  // The charge may still settle after we stop waiting; the settings page shows the real state.
  const leave = useCallback(() => {
    window.location.assign(returnPath)
  }, [returnPath])

  const follow = useCallback((attemptId, onSucceeded) => {
    setPhase(ATTEMPT_PHASE.PROCESSING)
    const deadline = Date.now() + FETCH_TIMEOUT_MS
    let fetchAttemptStatus = null

    const scheduleNextFetch = () => {
      if (Date.now() >= deadline) leave()
      else fetchTimerRef.current = setTimeout(fetchAttemptStatus, FETCH_INTERVAL_MS)
    }

    const handleResult = ({ status, code }) => {
      if (status === 'succeeded') {
        onSucceeded()
        leave()
      } else if (status !== 'failed') {
        scheduleNextFetch()
      } else if (ATTEMPT_RETRY_MESSAGES[code]) {
        retryWithNewQuote(ATTEMPT_RETRY_MESSAGES[code], code)
      } else {
        setPhase(ATTEMPT_PHASE.FAILED)
        fail(`${COMMIT_ERROR_KEY}.upgrade_failed`, code)
      }
    }

    fetchAttemptStatus = () => getData({
      path: commitUrl,
      params: { attempt: attemptId },
      isExpectedError: isAttemptNotFound,
      onSuccess: ({ data }) => handleResult(data),
      onError: (error) => (isAttemptNotFound(error) ? leave() : scheduleNextFetch()),
    })

    fetchAttemptStatus()
  }, [commitUrl, getData, leave, retryWithNewQuote, fail])

  const handleCommitError = useCallback((error, onSucceeded) => {
    const { code, attemptId, redirectTo } = errorData(error)

    if (code === 'upgrade_in_progress' && attemptId) {
      follow(attemptId, onSucceeded)
    } else if (COMMIT_RETRY_MESSAGES[code]) {
      retryWithNewQuote(COMMIT_RETRY_MESSAGES[code], code)
    } else if (redirectTo) {
      fail(pageErrorMessageKey(code), code)
      setTimeout(() => window.location.assign(redirectTo), REDIRECT_DELAY_MS)
    } else {
      fail('subscription_checkout.payment_error_toast', 'upgrade_commit_failed')
      onRetry()
    }
  }, [follow, retryWithNewQuote, fail, onRetry])

  const commit = useCallback(({ target, quotedAmount, onSucceeded = NOOP }) => {
    postData({
      path: commitUrl,
      data: { target, quotedAmount },
      isExpectedError: isExpectedCommitError,
      onSuccess: ({ data }) => follow(data.attemptId, onSucceeded),
      onError: (error) => handleCommitError(error, onSucceeded),
    })
  }, [commitUrl, postData, follow, handleCommitError])

  return { commit, phase }
}
